import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { modalPanelCls } from "../styles/ui";

// The one modal shell (UI audit Phase 2, Oct 12 2026). Replaces the copy-pasted
// `fixed inset-0 … onClick={close}` overlays, which had three bugs between them:
//   1. Backdrop close on `click` — a text selection started in a field and
//      released over the backdrop closed the form (and, where unguarded, lost it).
//      This closes only when the press STARTED on the backdrop.
//   2. No Escape and no scroll lock — the page behind kept scrolling.
//   3. No dialog semantics for screen readers.
//
// Portalled to <body>: escapes any transformed ancestor (a SidePanel), which
// would otherwise trap `position: fixed`, and is never inside a host <form>.
// Escape closes only the TOPMOST open modal, and a confirmDialog() above it
// (ConfirmHost captures Escape first) wins over both.
//
// `onClose` should be the GUARDED close when the body is an editable form
// (useDiscardGuard's `guard(...)`), so Escape and the backdrop prompt rather
// than discard. Pass `closeOnEscape={false}` for a form with no guard.

const stack = [];
let lockCount = 0;

// `2xl`/`full` (density pass, Nov 3 2026) are for FORMS: a PO or visit form
// at max-w-2xl used the middle third of a monitor and scrolled. Pair a wide
// size with a form whose grids are `form-grid` (container queries), or the
// extra width just makes each box wider.
const SIZES = {
  sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl",
  "2xl": "max-w-6xl", full: "sm:w-[96vw] max-w-[1600px]",
};
const LAYERS = { modal: "z-modal", dialog: "z-dialog", "dialog-top": "z-dialog-top", panel: "z-panel" };

export default function Modal({
  onClose,
  title,
  children,
  size = "lg",
  layer = "modal",
  align = "top",
  closeOnEscape = true,
  closeOnBackdrop = true,
  showClose = true,
  panelClassName = "",
  // `bare`: render `children` as the panel itself — only the backdrop
  // behaviour (portal, Escape, scroll lock, press-origin close) comes from here.
  // For an existing dialog whose panel markup is its own.
  bare = false,
  // bare only: a full-height sheet on a phone, like the default panel.
  fullBleed = false,
}) {
  const titleId = useId();
  const token = useRef({});
  const pressStartedOnBackdrop = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const me = token.current;
    stack.push(me);
    if (lockCount++ === 0) document.body.style.overflow = "hidden";
    // Document BUBBLE phase, on purpose: React's own handlers (a combobox
    // closing its list on Escape) run first, at the portal/root container, and
    // the event is stopped here before it reaches window — where SidePanel
    // listens. Without the stop, Escape in a modal opened over a panel closed
    // the PANEL, unmounting the modal and its half-typed form. ConfirmHost
    // listens on window CAPTURE, so a confirm above this still wins.
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (stack[stack.length - 1] !== me) return;
      e.stopPropagation();
      if (!closeOnEscape || e.defaultPrevented) return;
      e.preventDefault();
      onCloseRef.current?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const i = stack.indexOf(me);
      if (i >= 0) stack.splice(i, 1);
      if (--lockCount === 0) document.body.style.overflow = "";
    };
  }, [closeOnEscape]);

  // Two layers: the fixed, scrolling backdrop, and a min-h-full flex wrapper
  // that does the centring. Centring directly in the scroller (items-center on
  // an overflow-y-auto box) clips the TOP of a dialog taller than the screen —
  // it can't be scrolled back into view. A press on either layer is "backdrop".
  // A phone SHEET (the default panel, or a bare panel marked fullBleed)
  // stretches to the full screen height below sm:; a small bare dialog (a
  // confirm) stays a centred card with side padding.
  const sheet = !bare || fullBleed;
  // A sheet's wrapper is a DEFINITE full height on phones, so a panel sized
  // `h-full` (most of the converted ones) fills the screen as it did when the
  // fixed overlay was its parent — flex stretch alone skips `height: 100%`.
  const wrapCls = [
    "flex min-h-full w-full justify-center",
    sheet ? "h-full sm:h-auto" : "",
    sheet ? "px-0 sm:px-4" : "px-4",
    align === "center"
      ? (sheet ? "items-stretch sm:items-center sm:py-4" : "items-center py-4")
      : (sheet ? "items-stretch sm:items-start sm:pt-10" : "items-start sm:pt-10"),
  ].join(" ");
  const isBackdrop = (e) => e.target === e.currentTarget || e.target === e.currentTarget.firstElementChild;
  return createPortal(
    <div
      className={`fixed inset-0 ${LAYERS[layer] ?? "z-modal"} bg-ink/50 overflow-y-auto overscroll-contain`}
      role={bare ? "dialog" : undefined}
      aria-modal={bare ? "true" : undefined}
      onMouseDown={(e) => { pressStartedOnBackdrop.current = isBackdrop(e); }}
      onClick={(e) => {
        e.stopPropagation();
        if (closeOnBackdrop && pressStartedOnBackdrop.current && isBackdrop(e)) onClose?.();
        pressStartedOnBackdrop.current = false;
      }}
    >
      <div className={wrapCls}>
      {bare ? children : (
      // On a phone the wrapper is exactly one screen tall; a stretched panel
      // would be clamped to it and a long form would draw past its background.
      // self-start + min-h-full: fills the screen, grows with its content.
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={`${modalPanelCls} w-full ${SIZES[size] ?? size} self-start min-h-full sm:self-auto sm:min-h-0 rounded-none sm:rounded-xl sm:my-4 sm:max-h-[calc(100dvh-4rem)] sm:flex sm:flex-col ${panelClassName}`}
      >
        {(title || showClose) && (
          <div className="flex items-start justify-between gap-3 px-4 sm:px-5 pt-4 sm:pt-5 pb-3 shrink-0">
            {title && <h2 id={titleId} className="text-base font-semibold text-ink">{title}</h2>}
            {showClose && (
              <button
                type="button"
                onClick={() => onClose?.()}
                className="ml-auto inline-flex items-center justify-center -m-1.5 p-2.5 sm:p-1.5 rounded text-ink-muted hover:text-ink hover:bg-surface-inset focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                aria-label="Close"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {/* From sm: up the panel is capped to the screen and THIS scrolls,
            so the title stays put and a sticky FormActions row stays
            reachable. On a phone the whole sheet scrolls as before. */}
        <div className={`px-4 sm:px-5 pb-4 sm:pb-5 sm:min-h-0 sm:flex-1 sm:overflow-y-auto overscroll-contain ${(title || showClose) ? "" : "pt-4 sm:pt-5"}`}>
          {children}
        </div>
      </div>
      )}
      </div>
    </div>,
    document.body,
  );
}
